import type { ProjectReference } from '../project-reference';

/**
 * Canonical mock {@link ProjectReference} used by tests, MSW handlers
 * and the demo build. The value is small enough to render in lists
 * without overflow but high enough to exercise number formatting.
 */
export const MOCK_PROJECT_REFERENCE: ProjectReference = {
  id: 1,
  factory_id: 1,
  description: 'Laser-cut metal enclosure for industrial control panel',
  customer_name: 'Emirates Water & Power Projects',
  country_id: 1,
  start_date: '2024-10-01',
  end_date: '2024-12-15',
  industrial_subtype_ids: [11, 12],
  capability_ids: [11],
  value: 15000,
  on_demand: false,
};

/** Builds a {@link ProjectReference} from {@link MOCK_PROJECT_REFERENCE}. */
export function createMockProjectReference(
  overrides?: Partial<ProjectReference>
): ProjectReference {
  return { ...MOCK_PROJECT_REFERENCE, ...overrides };
}

/**
 * Builds a list of mock {@link ProjectReference} entries with unique
 * ids/descriptions so collection specs see distinct rows.
 */
export function createMockProjectReferenceList(
  count = 3,
  overrideAt?: (index: number) => Partial<ProjectReference>
): ProjectReference[] {
  return Array.from({ length: count }, (_, i) =>
    createMockProjectReference({
      id: i + 1,
      description: `Project reference ${i + 1}`,
      value: 10000 + i * 5000,
      ...overrideAt?.(i),
    })
  );
}

/**
 * AAE project references (factory id 43). Customer names and values are
 * invented demo data; destinations follow publicly stated export markets.
 */
export const MOCK_AAE_PROJECT_REFERENCES: ProjectReference[] = [
  createMockProjectReference({
    id: 43,
    factory_id: 43,
    description:
      'Welded and painted armoured cabins for an APC manufacturer — ' +
      'laser-cut ballistic plate, formed, welded, and finished in-house',
    customer_name: 'Confidential defence OEM',
    country_id: 1,
    start_date: '2024-02-01',
    end_date: '2024-11-30',
    industrial_subtype_ids: [4, 5],
    capability_ids: [41, 111, 141],
    value: 0,
    on_demand: true,
  }),
  createMockProjectReference({
    id: 44,
    factory_id: 43,
    description:
      'BlastRider blast-mitigation seating kits and armour brackets for export',
    customer_name: 'Confidential security integrator',
    country_id: 1,
    start_date: '2023-04-01',
    end_date: '2024-01-15',
    industrial_subtype_ids: [5, 7],
    capability_ids: [111, 211, 141],
    value: 0,
    on_demand: true,
  }),
  createMockProjectReference({
    id: 45,
    factory_id: 43,
    description:
      'Chassis brackets and architectural screen plates — laser-cut, formed, and powder-coated',
    customer_name: 'Gulf Transport Components',
    country_id: 1,
    start_date: '2025-01-10',
    end_date: '2025-06-20',
    industrial_subtype_ids: [3, 5],
    capability_ids: [111, 121, 211],
    value: 420000,
    on_demand: false,
  }),
];
